// -----------------------------------------------------------------------------
// src/pages/settings/User.jsx
// FULL component — creator‑linking logic added (no code cut ✂️)
// -----------------------------------------------------------------------------

import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  Tab,
} from "@mui/material";
import { SketchPicker } from "react-color";
import EditIcon from "@mui/icons-material/Edit";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PersonIcon from "@mui/icons-material/Person";
import LinkIcon from "@mui/icons-material/Link";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PaletteIcon from "@mui/icons-material/Palette";
import ApiIcon from "@mui/icons-material/Api";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TeamIcon from "@mui/icons-material/Group";
import { GoogleLogin } from "@react-oauth/google";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";

import StyledTabs from "../../Components/StyledTabs";
import TabPanel from "../../Components/TabPanel";
import DeveloperPanel from "./ApiServices/developerPanel";
import UploadFileDialog from "./Users/sepcupload";
import NotifSettings from "./Users/notif_settings";
import MyTeam from "./Users/myteam";
import useAlertDialog from "../../Components/useAlertDialog";
import AlertDialog from "../../Components/AlertDialog";

import client from "../../API";
import ppfLogo from "../../Components/globalAssets/ppfLogo.png";
import { switchRole } from "../../API/roleSwitcher";
import { useNavigate } from "react-router-dom";

/* ────────────────────────────────────────────────────────────────────────── */
/* main component                                                           */
/* ────────────────────────────────────────────────────────────────────────── */

const User = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = searchParams.get("tab") || "user";
  const navigate = useNavigate();

  const setTabValue = (newValue) => setSearchParams({ tab: newValue });

  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  /* ───────── dialogs & alerts ───────── */
  const { dialogState, openDialog } = useAlertDialog();
  const [uploadDialogOpen, setUploadDialogOpen]       = useState(false);
  const [creditDialogOpen, setCreditDialogOpen]       = useState(false);

  /* ───────── user profile state ──────── */
  const [userData, setUserData] = useState({
    firstName : "",
    lastName  : "",
    email     : "",
    username  : "",
    address   : "",
    phphref   : "",
    phoneNumber: "",        // ← add this
  });

  /* ───────── appearance prefs ───────── */
  const [appearanceSettings, setAppearanceSettings] = useState({
    darkMode: null,
    fontPreference: "default",
    uiDensity: "normal",
    themeColors: {
      primary: prefersDarkMode ? "#cb534f" : "#7b2a2a",
      secondary: "#646aff",
    },
  });

  /* ───────── creator‑linking state ──── */
  const [creatorHandleInput, setCreatorHandleInput] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [creatorLinked, setCreatorLinked] = useState(false);
  const [linkedHandle, setLinkedHandle]   = useState(null);

  /* ───────── remote queries ─────────── */
  const { data: userInfo } = useQuery(["user"], client.users.fetchUser, {
    onSuccess: (data) => {
      if (!data) return;
      setUserData((prev) => ({
        ...prev,
        firstName: data.first_name || "",
        lastName : data.last_name  || "",
        email    : data.email      || "",
        username : data.username   || "",
        address  : data.address    || "",
        phphref  : data.phphref    || "",
        phoneNumber: data.phone_number || "",  // ← map it here
      }));
    },
  });

  /*  company info, ledger, settings (unchanged) … */
  const {
    data: companyData,
    isLoading: companyLoading,
  } = useQuery(
    ["company", userInfo?.company_id],
    () => client.companies.listFetch({ id: userInfo?.company_id }),
    { enabled: !!userInfo?.company_id },
  );

  const {
    data: ledgerEntries,
    isLoading: ledgerLoading,
    isError:   ledgerError,
    error:     ledgerErrorObj,
  } = useQuery(
    ["creditLedger", userInfo?.company_id],
    () => client.creditLedger.fetch(userInfo.company_id),
    { enabled: !!userInfo?.company_id },
  );

  const { data: userSettings } = useQuery(["userSettings"], () =>
    client.users.fetchUserSettings()
  );

  /* ─── fetch global‑user linkage on mount ─── */
  useQuery(["globalUser"], client.users.fetchGlobalUser, {
    onSuccess: (g) => {
      if (!g) return;
      if (g.creator_username) {
        setCreatorLinked(true);
        setLinkedHandle(g.creator_username);
      }
    },
  });

  /* ───────── mutations ───────── */
  const saveUserMutation = useMutation(client.users.updateUser, {
    onSuccess: () => queryClient.invalidateQueries("user"),
    onError  : (error) =>
      openDialog("Error", error?.response?.data?.error || error.message),
  });

  const saveSettingsMutation = useMutation(
    (payload) =>
      userSettings?.id
        ? client.users.updateUserSettings({ id: userSettings.id, ...payload })
        : client.users.createUserSettings(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["userSettings", "user"]);
      },
      onError: (error) =>
        openDialog("Error", error?.response?.data?.error || error.message),
    }
  );

  const linkCreatorMutation = useMutation(client.users.linkCreatorAccount, {
    onSuccess: (resp) => {
      setCreatorLinked(true);
      setLinkedHandle(resp.creator_username);
      queryClient.invalidateQueries("globalUser");
      openDialog("Success", "Creator account linked successfully!");
    },
    onError: (error) => {
      openDialog("Error", error?.response?.data?.error || error.message);
    },
    onSettled: () => setLinkBusy(false),
  });

  /* ───────── side‑effects ───────── */
  useEffect(() => {
    if (userSettings?.id) {
      setAppearanceSettings({
        darkMode      : userSettings.dark_mode,
        fontPreference: userSettings.font_preference,
        uiDensity     : userSettings.ui_density,
        themeColors   : userSettings.theme_colors || {
          primary: "#1976d2",
          secondary: "#9c27b0",
        },
      });
    }
  }, [userSettings]);

  /* ───────── handlers ───────── */
  const handleUserInfoChange = (e) =>
    setUserData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSaveUserInfo = async () => {
    await saveUserMutation.mutateAsync({
      first_name: userData.firstName,
      last_name : userData.lastName,
      email     : userData.email,
      username  : userData.username,
      address   : userData.address,
      phphref   : userData.phphref,
      phone_number: userData.phoneNumber,   // ← send it
    });
  };

  const handleSaveAppearance = async () => {
    await saveSettingsMutation.mutateAsync({
      dark_mode     : appearanceSettings.darkMode,
      font_preference: appearanceSettings.fontPreference,
      ui_density    : appearanceSettings.uiDensity,
      theme_colors  : appearanceSettings.themeColors,
    });
  };

  const handleLinkCreator = () => {
    if (!creatorHandleInput.trim()) {
      openDialog("Error", "Please enter a creator username");
      return;
    }
    setLinkBusy(true);
    linkCreatorMutation.mutate({ creator_username: creatorHandleInput.trim() });
  };

  const switchToCreatorDashboard = () => {
    switchRole("creator");             // utility flips active role cookie
    navigate("/creatorconnect/start");    
  };

  /* ─────────────────────────────────────────── rendered JSX ───────────────── */

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1280,
        margin: "auto",
        p: 3,
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      <AlertDialog alertState={dialogState} />

      {/* --------- company header card (unchanged) --------- */}
      <Card
        sx={{
          background: "linear-gradient(135deg, #2c3e50 0%, #3498db 100%)",
          color: "white",
          mb: 3,
          borderRadius: 3,
          boxShadow: 3,
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
            <Avatar
              src={companyData?.phphref || ppfLogo}
              sx={{ width: 96, height: 96, border: "3px solid white", boxShadow: 3 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {companyData?.name || "Your Company"}
              </Typography>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Chip
                  label={`Balance: $${companyData?.balance || "0.00"}`}
                  sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 600 }}
                />
                <Chip
                  label={`Credit Line: $${companyData?.credit_line || "0.00"}`}
                  sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 600 }}
                />
                <Chip
                  label={`Rate: ${companyData?.nominal_interest_rate || "0.00"}%`}
                  sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 600 }}
                />
              </Box>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RequestQuoteIcon />}
              onClick={() => setCreditDialogOpen(true)}
            >
              Adjust Credit
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* --------- sidebar + tab panels --------- */}
      <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", lg: "row" } }}>
        <Card sx={{ width: { lg: 240 }, flexShrink: 0, borderRadius: 3, boxShadow: 3 }}>
          <StyledTabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            orientation="vertical"
            variant="scrollable"
          >
            <Tab icon={<PersonIcon />} label="Profile" value="user" />
            <Tab icon={<PaletteIcon />} label="Appearance" value="appearance" />
            <Tab icon={<TeamIcon />} label="My Team" value="team" />
            <Tab icon={<NotificationsIcon />} label="Notifications" value="notif" />
            <Tab icon={<ApiIcon />} label="Developers" value="dev" />
          </StyledTabs>
        </Card>

        <Box sx={{ flex: 1 }}>
          {/* ───────── Profile tab ───────── */}
          <TabPanel value={tabValue} index="user">
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                {/* avatar + name */}
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Box
                    sx={{
                      position: "relative",
                      display: "inline-block",
                      "&:hover .edit-overlay": { opacity: 1 },
                    }}
                  >
                    <Avatar
                      src={userData.phphref || ppfLogo}
                      sx={{ width: 120, height: 120, fontSize: "2.5rem", mb: 2 }}
                    />
                    <IconButton
                      onClick={() => setUploadDialogOpen(true)}
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        backgroundColor: "background.paper",
                        "&:hover": { backgroundColor: "action.selected" },
                        opacity: 0.8,
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {userData.firstName} {userData.lastName}
                  </Typography>
                  <Typography color="text.secondary">@{userData.username}</Typography>
                </Box>

                {/* basic info form */}
                <Grid container spacing={3}>
                  {[
                    { name: "firstName", label: "First Name", xs: 12, md: 6 },
                    { name: "lastName",  label: "Last Name",  xs: 12, md: 6 },
                    { name: "email",     label: "Email",      xs: 12, md: 6, type: "email" },
                    { name: "username",  label: "Username",   xs: 12, md: 6 },
                  ].map((f) => (
                    <Grid item xs={f.xs} md={f.md} key={f.name}>
                      <TextField
                        fullWidth
                        label={f.label}
                        type={f.type || "text"}
                        variant="outlined"
                        size="medium"
                        value={userData[f.name]}
                        onChange={handleUserInfoChange}
                        name={f.name}
                      />
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      variant="outlined"
                      size="medium"
                      multiline
                      rows={2}
                      value={userData.address}
                      onChange={handleUserInfoChange}
                      name="address"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      type="tel"
                      variant="outlined"
                      size="medium"
                      name="phoneNumber"
                      value={userData.phoneNumber}
                      onChange={handleUserInfoChange}
                    />
                  </Grid>

                  {/* link Google account */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Add Google Account as login method
                    </Typography>
                    <Box sx={{ width: "min-content" }}>
                      <GoogleLogin
                        onSuccess={({ credential }) =>
                          client.users.updateGoogle({ google_token: credential })
                        }
                        onError={(e) => openDialog("Error", e.error)}
                      />
                    </Box>
                  </Grid>

                  {/* creator account LINKING */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 4 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Creator Account
                    </Typography>

                    {creatorLinked ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                          p: 2,
                          border: "1px dashed",
                          borderColor: "divider",
                          borderRadius: 2,
                          backgroundColor: "action.hover",
                        }}
                      >
                        <Chip
                          icon={<LinkIcon />}
                          label={`Linked to @${linkedHandle}`}
                          color="success"
                          sx={{ fontWeight: 600 }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={switchToCreatorDashboard}
                          startIcon={<PersonIcon />}
                        >
                          Switch to Creator Dashboard
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <TextField
                          label="Creator Username"
                          variant="outlined"
                          size="medium"
                          value={creatorHandleInput}
                          onChange={(e) => setCreatorHandleInput(e.target.value)}
                        />
                        <Button
                          variant="contained"
                          startIcon={<PersonAddIcon />}
                          disabled={linkBusy}
                          onClick={handleLinkCreator}
                        >
                          {linkBusy ? <CircularProgress size={22} /> : "Link Account"}
                        </Button>
                      </Box>
                    )}
                  </Grid>

                  {/* save btn */}
                  <Grid item xs={12} sx={{ textAlign: "right" }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleSaveUserInfo}
                      disabled={saveUserMutation.isLoading}
                    >
                      {saveUserMutation.isLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </TabPanel>

          {/* ───────── Appearance tab (unchanged) ───────── */}
          <TabPanel value={tabValue} index="appearance">
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                  Appearance Settings
                </Typography>

                <Grid container spacing={3}>
                  {/* theme mode */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="Theme Mode"
                      value={appearanceSettings.darkMode}
                      onChange={(e) =>
                        setAppearanceSettings((prev) => ({
                          ...prev,
                          darkMode: e.target.value,
                        }))
                      }
                      variant="outlined"
                      size="medium"
                    >
                      <MenuItem value={null}>System Default</MenuItem>
                      <MenuItem value={true}>Dark</MenuItem>
                      <MenuItem value={false}>Light</MenuItem>
                    </TextField>
                  </Grid>

                  {/* UI density */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="UI Density"
                      value={appearanceSettings.uiDensity}
                      onChange={(e) =>
                        setAppearanceSettings((prev) => ({
                          ...prev,
                          uiDensity: e.target.value,
                        }))
                      }
                      variant="outlined"
                      size="medium"
                    >
                      <MenuItem value="compact">Compact</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="comfortable">Comfortable</MenuItem>
                    </TextField>
                  </Grid>

                  {/* font */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="Font Family"
                      value={appearanceSettings.fontPreference}
                      onChange={(e) =>
                        setAppearanceSettings((prev) => ({
                          ...prev,
                          fontPreference: e.target.value,
                        }))
                      }
                      variant="outlined"
                      size="medium"
                    >
                      <MenuItem value="default">System Default</MenuItem>
                      <MenuItem value="roboto">Roboto</MenuItem>
                      <MenuItem value="open-sans">Open Sans</MenuItem>
                      <MenuItem value="montserrat">Montserrat</MenuItem>
                    </TextField>
                  </Grid>

                  {/* colors */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Color Customization
                    </Typography>
                    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                      {["primary", "secondary"].map((key) => (
                        <Box key={key}>
                          <FormHelperText sx={{ mb: 1 }}>
                            {key.charAt(0).toUpperCase() + key.slice(1)} Color
                          </FormHelperText>
                          <SketchPicker
                            color={appearanceSettings.themeColors[key]}
                            onChangeComplete={(color) =>
                              setAppearanceSettings((prev) => ({
                                ...prev,
                                themeColors: { ...prev.themeColors, [key]: color.hex },
                              }))
                            }
                          />
                        </Box>
                      ))}
                    </Box>
                  </Grid>

                  {/* save prefs btn */}
                  <Grid item xs={12} sx={{ textAlign: "right" }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleSaveAppearance}
                      disabled={saveSettingsMutation.isLoading}
                    >
                      {saveSettingsMutation.isLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Save Preferences"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </TabPanel>

          {/* ───────── Dev / Team / Notif panels (unchanged) ───────── */}
          <TabPanel value={tabValue} index="dev">
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <DeveloperPanel />
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index="team">
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                  My Team
                </Typography>
                <MyTeam />
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index="notif">
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                  Notification Settings
                </Typography>
                <NotifSettings />
              </CardContent>
            </Card>
          </TabPanel>
        </Box>
      </Box>

      {/* --------- credit dialog (unchanged) --------- */}
      <CreditDialog
        open={creditDialogOpen}
        onClose={() => setCreditDialogOpen(false)}
        ledgerEntries={ledgerEntries}
        ledgerLoading={ledgerLoading}
        ledgerError={ledgerError}
        ledgerErrorObj={ledgerErrorObj}
        companyId={userInfo?.company_id}
      />

      {/* --------- avatar upload dialog --------- */}
      <UploadFileDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={(fileData) =>
          setUserData((prev) => ({ ...prev, phphref: fileData.url }))
        }
      />
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/* Separated credit dialog component (kept identical to original)            */
/* -------------------------------------------------------------------------- */

const CreditDialog = ({
  open,
  onClose,
  ledgerEntries,
  ledgerLoading,
  ledgerError,
  ledgerErrorObj,
  companyId,
}) => {
  const queryClient = useQueryClient();
  const [requestedCreditLine, setRequestedCreditLine] = useState("");
  const [requestedInterestRate, setRequestedInterestRate] = useState("");
  const [reason, setReason] = useState("");

  const postCreditRequestMutation = useMutation(
    (input) => client.creditLedger.postRequest(input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["creditLedger", companyId]);
        onClose();
        setReason("");
      },
      onError: (error) =>
        alert(error?.response?.data?.error || error.message),
    }
  );

  const handleSubmit = () => {
    postCreditRequestMutation.mutate({
      company_id: companyId,
      requested_credit_line: Number(requestedCreditLine),
      requested_interest_rate: Number(requestedInterestRate),
      reason: reason || "User request",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 3, width: "100%", maxWidth: 1200 } }}>
      <DialogTitle>Adjust Credit & Interest Rate</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Below is the historical credit ledger for your company. You can also request a change to your credit line and/or nominal interest rate.
        </DialogContentText>

        {ledgerLoading && <CircularProgress size={24} sx={{ m: 2 }} />}
        {ledgerError && <Typography color="error">Error loading ledger: {ledgerErrorObj?.message}</Typography>}
        {ledgerEntries?.length > 0 && (
          <Box sx={{ maxHeight: 200, overflowY: "auto", mb: 2, border: "1px solid #ccc", borderRadius: 1, p: 1 }}>
            {ledgerEntries.map((entry) => (
              <Box key={entry.id} sx={{ mb: 1, p: 1, borderBottom: "1px solid #eee" }}>
                <Typography variant="body2">
                  <strong>{entry.operation_type.toUpperCase()}</strong> on {new Date(entry.created_at).toLocaleString()}
                </Typography>
                <Typography variant="caption">
                  Change: {entry.change_amount} | New Credit: {entry.new_credit_line} | New Rate: {entry.new_interest_rate}%
                </Typography>
                {entry.reason && (
                  <Typography variant="caption" display="block">
                    Reason: {entry.reason}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Requested Credit Line"
              type="number"
              fullWidth
              value={requestedCreditLine}
              onChange={(e) => setRequestedCreditLine(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Requested Interest Rate"
              type="number"
              fullWidth
              value={requestedInterestRate}
              onChange={(e) => setRequestedInterestRate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Reason for Change"
              fullWidth
              multiline
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={postCreditRequestMutation.isLoading}>
          {postCreditRequestMutation.isLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit Request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default User;
